import { useQuery } from '@tanstack/react-query';
import {
  Button,
  DataTable,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  SectionLabel,
  Spinner,
  type DataTableColumn,
} from '@wundo/ui';
import type { TrackedPageRoute } from '@wundo/shared/pageVisit.ts';
import {
  fetchPageUsageUsers,
  type DashboardScope,
  type PageUsageUser,
  type PageUsageVisitor,
} from '@/lib/dashboard.ts';
import { formatDate } from '@/utils/dateUtils.ts';
import { TRACKED_PAGES } from './trackedPages.ts';

function displayName(user: PageUsageUser): string {
  return (
    [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email || 'Unknown'
  );
}

const USED_COLUMNS: DataTableColumn<PageUsageVisitor>[] = [
  { key: 'name', header: 'Name', render: displayName },
  { key: 'visit_count', header: 'Visits', align: 'end', width: 'w-20', nowrap: true },
  {
    key: 'last_visit_at',
    header: 'Last visit',
    align: 'end',
    width: 'w-32',
    nowrap: true,
    render: (row) =>
      formatDate(row.last_visit_at, { day: 'numeric', month: 'short', year: 'numeric' }),
  },
];

const NEVER_COLUMNS: DataTableColumn<PageUsageUser>[] = [
  { key: 'name', header: 'Name', render: displayName },
  { key: 'email', header: 'Email' },
];

export interface PageUsageUsersDialogProps {
  /** The page being looked at; null closes the dialog. */
  route: TrackedPageRoute | null;
  scope: DashboardScope;
  onClose: () => void;
}

/** One page opened up: who used it, and who uses the app but never found it. */
export function PageUsageUsersDialog({
  route,
  scope,
  onClose,
}: PageUsageUsersDialogProps) {
  const users = useQuery({
    queryKey: [
      'pageUsageUsers',
      route,
      scope.cohortId ?? null,
      scope.organisationId ?? null,
    ],
    queryFn: () => fetchPageUsageUsers(scope, route!),
    enabled: route !== null,
    throwOnError: true,
  });

  return (
    <Dialog
      open={route !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent size='lg' layout='scroll'>
        {route && (
          <>
            <DialogHeader>
              <DialogTitle>{TRACKED_PAGES[route].label}</DialogTitle>
            </DialogHeader>

            <div className='flex flex-col gap-6 px-5 py-4'>
              {users.data === undefined ? (
                <p className='flex items-center gap-2 text-sm text-ink-muted'>
                  <Spinner />
                  Loading users…
                </p>
              ) : (
                <>
                  <section className='flex flex-col gap-2'>
                    <SectionLabel as='h3'>{`Used it (${users.data.data.used.length})`}</SectionLabel>
                    <DataTable<PageUsageVisitor>
                      label='Users who opened this page'
                      columns={USED_COLUMNS}
                      rows={users.data.data.used}
                      rowKey={(row) => row.id}
                      emptyState={<EmptyState title='Nobody opened this page yet' />}
                    />
                  </section>

                  <section className='flex flex-col gap-2'>
                    <SectionLabel as='h3'>{`Never opened it (${users.data.data.never.length})`}</SectionLabel>
                    <DataTable<PageUsageUser>
                      label='Users who never opened this page'
                      columns={NEVER_COLUMNS}
                      rows={users.data.data.never}
                      rowKey={(row) => row.id}
                      emptyState={<EmptyState title='Everybody opened this page' />}
                    />
                  </section>
                </>
              )}
            </div>

            <DialogFooter>
              <Button variant='quiet' onClick={onClose}>
                Close
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
